/** Attributes carried in the tree vector tiles (see pipeline/build-tiles.sh). */
export interface TreeProperties {
  tree_id: number;
  spc_common?: string;
  spc_latin?: string;
  tree_dbh?: number;
  status?: string;
  health?: string;
  address?: string;
  nta_name?: string;
  boroname?: string;
  /**
   * 1 when the archive holds a non-empty point cloud for this tree, 0 when it
   * holds `[]` or nothing at all. 17% of the census is 0. Baked in by
   * pipeline/flag-trees.py so the map can grey those trees rather than letting
   * a visitor find out by clicking.
   */
  has_canopy?: number;
}

/**
 * A tree the user has selected, with its LiDAR canopy loaded.
 *
 * The canopy itself is not here. Clouds are flat Float64Arrays owned by the
 * classifier (lib/canopyClient.ts), which hands them to a worker and never
 * copies them back; this side keeps only what the UI needs to draw and count.
 */
export interface SelectedTree {
  id: number;
  lon: number;
  lat: number;
  properties: TreeProperties;
  /** LiDAR returns in this tree's cloud. */
  pointCount: number;
}

/**
 * A building footprint that casts a ground shadow.
 *
 * Every building in the viewport is one of these now, not only the ones the
 * user clicked. Clicking still matters -- it focuses the statistics on that
 * building -- but it no longer gates whether classification happens at all.
 */
export interface SelectedBuilding {
  bin: string;
  /** Outer ring, [lon, lat] pairs. */
  footprint: Array<[number, number]>;
  /** Roof height in feet, as stored in the `heightroof` attribute. */
  heightRoofFeet: number;
}

/**
 * `analysis` colors canopy and ground shadow by what each point shades.
 * `foliage` drops the analysis entirely -- plain green canopy, neutral shadow --
 * so canopy shape can be inspected without the color coding reading as data.
 */
export type ShadeMode = 'analysis' | 'foliage';

/**
 * Which model classifies canopy points.
 *
 * `2023` is the published flat projection ported in lib/shadow.ts: a point is
 * tested against building shadow hulls in plan only, as if it stood at ground
 * level. `occluded` is the height-aware model in lib/occlusion.ts, which is the
 * default. The old one stays selectable so any published figure can be
 * reproduced on demand -- see docs/shading-model.md.
 */
export type ShadingModel = '2023' | 'occluded';

export interface CanopyStats {
  trees: number;
  points: number;
  /** Canopy shading open ground. */
  other: number;
  /** Canopy shading a building. */
  shading: number;
  /** Canopy itself in shade. */
  shaded: number;
  /** How many buildings the figures were measured against. */
  buildings: number;
  /** True when a specific building is focused rather than the whole viewport. */
  focused: boolean;
  /** Which model produced the figures above. */
  model: ShadingModel;
}

export const EMPTY_STATS: CanopyStats = {
  trees: 0,
  points: 0,
  other: 0,
  shading: 0,
  shaded: 0,
  buildings: 0,
  focused: false,
  model: 'occluded',
};
